import {
  Modal,
  Backdrop,
  Fade,
  Paper,
  Box,
  Button,
  CircularProgress,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { discoveryApiRef } from '@backstage/core-plugin-api';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useState } from 'react';

interface UnlinkConfirmationModalProps {
  open: boolean;
  pattern: string;
  onClose: (success: boolean) => void;
}

const useStyles = makeStyles(theme => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    maxWidth: '800px',
  },
}));

export const UnlinkConfirmationModal = ({
  open,
  pattern,
  onClose,
}: UnlinkConfirmationModalProps) => {
  const classes = useStyles();
  const { entity } = useEntity();
  const entityRef = stringifyEntityRef(entity);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUnlink = async () => {
    try {
      setError(null);
      setLoading(true);

      const baseUrl = await discoveryApi.getBaseUrl('redis-analysis');
      const encodedEntityRef = encodeURIComponent(entityRef);
      const encodedPattern = encodeURIComponent(pattern);
      const res = await fetchApi.fetch(
        `${baseUrl}/unlink-pattern/${encodedEntityRef}/${encodedPattern}`,
        {
          method: 'DELETE',
        },
      );
      const response = await res.json();
      if ('error' in response) {
        throw new Error(response.error);
      }
      onClose(true);
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
      } else {
        setError(JSON.stringify(e));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      className={classes.modal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      onClose={() => onClose(false)}
    >
      <Fade in={open}>
        <Paper className={classes.paper}>
          <Typography variant="h6">Unlink Key/Pattern Confirmation</Typography>
          <Box display="flex" flexDirection="column" gridGap={16} my={3}>
            <Typography>
              Please ensure you'd like to unlink <b>{pattern}</b> pattern!
            </Typography>
            <Typography>
              Post the key/pattern you are deleting in <b>#team-eng</b> or
              <b>#eng-help-db-query-approval-requests</b> if you have any
              concerns.
            </Typography>
            <Typography>
              If you have any issues or questions about this tool please contact{' '}
              <b>@platformenghelp</b> on the <b>#ask-platform-eng</b> channel on
              Slack!
            </Typography>
            <Typography>Thank you & Happy Unlinking! 🚀</Typography>
          </Box>
          <Box display="flex" justifyContent="flex-end" gridGap={8}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => onClose(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!!loading}
              endIcon={
                loading ? <CircularProgress size={20} color="inherit" /> : null
              }
              onClick={handleUnlink}
            >
              Unlink
            </Button>
          </Box>
          {error && (
            <Box>
              <Typography color="error">
                <b>Error:</b> {error}
              </Typography>
            </Box>
          )}
        </Paper>
      </Fade>
    </Modal>
  );
};
